/**
 * Mounted once in RootLayout: the round launcher on app pages, then the panel
 * and the guide player as separate chunks loaded on first need.
 *
 * A guide that has to continue on another page arrives as ?assistantGuide=<id>
 * (the planner opens such pages in a new tab); this picks it up and removes it
 * from the address bar.
 */
import React, { Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAssistantStore } from './assistantStore';
import AssistantLauncher from './AssistantLauncher';

const AssistantPanel = React.lazy(() => import('./AssistantPanel'));
const GuidePlayer = React.lazy(() => import('./GuidePlayer'));

const GUIDE_PARAM = 'assistantGuide';

const AssistantRoot: React.FC = () => {
  const open = useAssistantStore((s) => s.open);
  const guideId = useAssistantStore((s) => s.guideId);
  const startGuide = useAssistantStore((s) => s.startGuide);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get(GUIDE_PARAM);
    if (!id || !/^g\.[\w.-]{1,40}$/.test(id)) return;
    startGuide(id);
    params.delete(GUIDE_PARAM);
    const rest = params.toString();
    window.history.replaceState(window.history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
  }, [location.search, location.pathname, location.hash, startGuide]);

  return (
    <>
      <AssistantLauncher />
      {(open || guideId) && (
        <Suspense fallback={null}>
          {open && <AssistantPanel />}
          {guideId && <GuidePlayer />}
        </Suspense>
      )}
    </>
  );
};

export default AssistantRoot;
